export type Language = 'en' | 'es' | 'pt' | 'fr' | 'ar' | 'ja' | 'ko' | 'id' | 'hi';

export const RTL_LANGUAGES: Language[] = ['ar'];

export const SUPPORTED_LANGUAGES: { code: Language; label: string; nativeLabel: string; flag: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', flag: '🇧🇷' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', flag: '🇸🇦' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', flag: '🇰🇷' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa', flag: '🇮🇩' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', flag: '🇮🇳' },
];

export interface TranslationKeys {
  nav: {
    home: string;
    about: string;
    faq: string;
    pricing: string;
    howToUse: string;
    giftStore: string;
    blog: string;
    login: string;
    signup: string;
    dashboard: string;
    logout: string;
  };
  hero: {
    tagline: string;
    headline: string;
    subheadline: string;
    cta: string;
    ctaSecondary: string;
    socialProof: string;
  };
  values: {
    title: string;
    problem: { title: string; description: string };
    solution: { title: string; description: string };
    impact: { title: string; description: string };
  };
  templates: {
    title: string;
    subtitle: string;
    classicElegance: string;
    modernWarmth: string;
    boldStatement: string;
    intimateJournal: string;
  };
  howItWorks: {
    title: string;
    step1: { title: string; description: string };
    step2: { title: string; description: string };
    step3: { title: string; description: string };
    step4: { title: string; description: string };
  };
  howToUse: {
    title: string;
    subtitle: string;
    create: {
      title: string;
      subtitle: string;
      step1: { label: string; desc: string };
      step2: { label: string; desc: string };
      step3: { label: string; desc: string };
      step4: { label: string; desc: string };
      step5: { label: string; desc: string };
    };
    dashboard: {
      title: string;
      subtitle: string;
      viewAll: { label: string; desc: string };
      editAnytime: { label: string; desc: string };
      copyLinks: { label: string; desc: string };
      viewAsMagazine: string;
    };
    aiFeatures: {
      title: string;
      subtitle: string;
      messageWriting: { label: string; desc: string };
      reminders: { label: string; desc: string };
      autoCompose: { label: string; desc: string };
    };
    gamification: {
      title: string;
      subtitle: string;
      levelsTitle: string;
      badgesTitle: string;
      levels: {
        heartOpener: string;
        soulConnector: string;
        loveAmbassador: string;
        gratitudeChampion: string;
        legacyMaker: string;
      };
      badges: {
        firstExpression: string;
        loveAmbassador: string;
        birthdayChampion: string;
        consistencyStar: string;
      };
      jacKeyLines: {
        firstExpression: string;
        relationshipCelebrant: string;
        birthdayChampion: string;
        friendshipKeeper: string;
        consistencyStar: string;
        loveAmbassador: string;
      };
      helperMicrocopy: string;
      progressContext: {
        nextMilestone: string;
        maxLevel: string;
      };
      yourBadges: string;
      pointsTotal: string;
      ptsToGo: string;
      achieved: string;
      next: string;
      progressEncouragement: string;
      quietModeOn: string;
      tiers: {
        awareness: string;
        presence: string;
        continuity: string;
        beyond: string;
      };
      ptsTo: string;
      highestTier: string;
      softEncouragement: {
        message: string;
        cta: string;
        dismiss: string;
      };
    };
  };
  examples: {
    title: string;
    subtitle: string;
    to: string;
    from: string;
  };
  video: {
    badge: string;
    title: string;
    subtitle: string;
    playButton: string;
    stat1: string;
    stat2: string;
    stat3: string;
  };
  pricing: {
    title: string;
    subtitle: string;
    popular: string;
    guarantee: string;
    monthly: string;
    annual: string;
    savePercent: string;
    plans: {
      free: {
        name: string;
        price: string;
        period: string;
        description: string;
        features: string[];
        cta: string;
      };
      plus: {
        name: string;
        price: string;
        period: string;
        annualPrice: string;
        annualPeriod: string;
        description: string;
        features: string[];
        cta: string;
      };
      legacy: {
        name: string;
        price: string;
        period: string;
        annualPrice: string;
        annualPeriod: string;
        description: string;
        features: string[];
        cta: string;
      };
    };
  };
  cta: {
    badge: string;
    title: string;
    subtitle: string;
    button: string;
    viewPricing: string;
    noCard: string;
  };
  footer: {
    tagline: string;
    about: string;
    faq: string;
    pricing: string;
    howToUse: string;
    giftStore: string;
    privacy: string;
    terms: string;
    contact: string;
    copyright: string;
  };
  auth: {
    loginTitle: string;
    loginSubtitle: string;
    signupTitle: string;
    signupSubtitle: string;
    email: string;
    password: string;
    confirmPassword: string;
    fullName: string;
    loginButton: string;
    signupButton: string;
    forgotPassword: string;
    noAccount: string;
    hasAccount: string;
    orContinueWith: string;
    google: string;
    apple: string;
    resetPassword: string;
    resetPasswordSubtitle: string;
    sendResetLink: string;
    backToLogin: string;
    newPassword: string;
    newPasswordSubtitle: string;
    newPasswordLabel: string;
    confirmNewPassword: string;
    passwordMismatch: string;
    passwordUpdated: string;
    passwordUpdatedSubtitle: string;
    updatePasswordButton: string;
    resetLinkExpired: string;
  };
  common: {
    loading: string;
    error: string;
    retry: string;
    cancel: string;
    save: string;
    delete: string;
    edit: string;
    create: string;
    share: string;
    preview: string;
    publish: string;
    draft: string;
    pending: string;
    approved: string;
    rejected: string;
  };
  about: {
    badge: string;
    title: string;
    subtitle: string;
    story1: string;
    story2: string;
    story3: string;
    story4: string;
    story5: string;
    highlight: string;
    mission: string;
    valuesTitle: string;
    value1Title: string;
    value1Desc: string;
    value2Title: string;
    value2Desc: string;
    value3Title: string;
    value3Desc: string;
    value4Title: string;
    value4Desc: string;
    ctaTitle: string;
    ctaSubtitle: string;
    ctaButton: string;
    ctaSecondary: string;
  };
  faq: {
    badge: string;
    title: string;
    subtitle: string;
    categoryGeneral: string;
    categoryFeatures: string;
    categoryPrivacy: string;
    categoryBilling: string;
    categoryBadges: string;
    q15: string; a15: string;
    badgesFaq: {
      intro: string;
      howEarnedTitle: string;
      howEarnedSubtitle: string;
      badges: {
        firstExpression: { name: string; earned: string; trigger: string; note?: string };
        relationshipCelebrant: { name: string; earned: string; trigger: string; note?: string };
        birthdayChampion: { name: string; earned: string; trigger: string; note?: string };
        friendshipKeeper: { name: string; earned: string; trigger: string; note: string };
        consistencyStar: { name: string; earned: string; trigger?: string; note?: string };
        loveAmbassador: { name: string; earned: string; trigger?: string; note?: string };
      };
      levelsTitle: string;
      levelsSubtitle: string;
      levels: {
        heartOpener: string;
        soulConnector: string;
        loveAmbassador: string;
        gratitudeChampion: string;
        legacyMaker: string;
      };
      pointsLabel: string;
      levelsNote: string;
      dateNoteTitle: string;
      dateNoteBody: string;
      finalNote: string;
    };
    q1: string; a1: string;
    q2: string; a2: string;
    q3: string; a3: string;
    q4: string; a4: string;
    q5: string; a5: string;
    q6: string; a6: string;
    q7: string; a7: string;
    q8: string; a8: string;
    q9: string; a9: string;
    q10: string; a10: string;
    q11: string; a11: string;
    q12: string; a12: string;
    q13: string; a13: string;
    q14: string; a14: string;
    contactTitle: string;
    contactSubtitle: string;
    contactButton: string;
    contactEmail: string;
  };
  jackey: {
    nudges: {
      firstExpression: string;
      relationshipCelebrant: string;
      birthdayChampion: string;
      friendshipKeeper: string;
      consistencyStar: string;
      loveAmbassador: string;
      legacyBuilder: string;
    };
  };
  // Component-specific keys for localized copy
  header: { tagline: string };
  settings: {
    settings: string;
    profile: string;
    preferences: string;
    importantDates: string;
    autoCompose: string;
    fullName: string;
    language: string;
    save: string;
    saving: string;
    saved: string;
    error: string;
  };
  creditDisplay: {
    heroHeadline: string;
    heroSubtext: string;
    credits: string;
    createNew: string;
    addMore: string;
    aiAssists: string;
    remaining: string;
  };
  founder: {
    badge: string;
    indicator: string;
    reminderBanner: string;
    reminderSecondary: string;
    reload: string;
    dismiss: string;
    expiryTitle: string;
    expiryText: string;
    expirySubtext: string;
    expiryCta: string;
  };
  treslog: {
    inlineHintText: string;
    inlineHintLink: string;
    upsellTitle: string;
    upsellText: string;
    upsellCta: string;
  };
  postPublish: {
    title: string;
    subtitle: string;
    share: string;
    dashboard: string;
  };
  mobile: {
    startWriting: string;
    speak: string;
  };
  security: {
    checkRequired: string;
    checkFailed: string;
    verificationFailed: string;
    retry: string;
  };
  reveal: {
    identityRevealed: string;
    sentBy: string;
    someoneWhoCares: string;
    revealingSoon: string;
    revealInDays: string;
    revealInOneDay: string;
  };
}
